// lib/apiClient.js

import axios from "axios";

// Simple memory cache (works for both server and browser)
// See below for SSR-safe variants if needed
let apiConfig = null;
let apiConfigPromise = null; // for in-flight deduplication

const base_url = process.env.NEXT_PUBLIC_CREDS;

/**
 * Loads API credentials/config ONCE per session & caches globally.
 * Fast: never double fetches, and all requests after the first are instant.
 */
export async function loadCreds() {
  if (apiConfig) return apiConfig; // Already loaded
  if (apiConfigPromise) return apiConfigPromise; // Wait for in-flight request

  apiConfigPromise = fetch(base_url)
    .then((res) => {
      if (!res.ok) throw new Error("Failed to load API config");
      return res.json();
    })
    .then((json) => {
      apiConfig = json;
      return apiConfig;
    })
    .finally(() => {
      apiConfigPromise = null; // Clean up after resolution
    });

  return apiConfigPromise;
}

// Axios instance factories (stateless, always return fresh instance)
export const getAxiosInstance = (baseUrl) =>
  axios.create({
    baseURL: baseUrl,
    timeout: 40000,
    headers: { "Content-Type": "application/json" },
  });

export const getAuthAxiosInstance = (baseUrl) => {
  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;
  return axios.create({
    baseURL: baseUrl,
    timeout: 40000,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
};

export const getContentAxiosInstance = (baseUrl) =>
  axios.create({
    baseURL: "https://prod.content.ideayatra.com/api/v1",
    timeout: 40000,
    headers: { "Content-Type": "application/json" },
  });

export const getContentAuthAxiosInstance = (baseUrl) =>
  axios.create({
    baseURL: baseUrl,
    timeout: 40000,
    headers: {
      "Content-Type": "application/json",
      // Add auth header logic here as needed
    },
  });

/**
 * Unified API caller using loaded creds and passed options.
 * Fast, robust, no repeated config fetches.
 */
export const callApi = async ({
  key,
  method,
  body = {},
  params = {},
  pathParam,
  useContent = false,
  auth = false,
}) => {
  // Config is globally memoized: never hits network twice per session!
  const credsRes = await loadCreds();
  const creds = credsRes.data;

  const apiPath = creds.apiPaths.find(
    (item) => item.key === key && item.apiMethod === method,
  );

  if (!apiPath) throw new Error(`API path not found: ${key}`);
  let url = apiPath.value;

  if (pathParam) url += `/${pathParam}`;
  if (pathParam === "6893bf367affa517ae74622e") {
    url += `?isWeb=true`;
  } else if (pathParam === "68d00f022b247c894e097334") {
    url += `?isWeb=true`;
  } else if (pathParam === "695127c56183535423530a70") {
    url += `?isWeb=true`;
  } else if (pathParam === "691380a00db0c695933359c2") {
    url += `?isWeb=true`;
  }

  let instance;
  if (auth && useContent)
    instance = getContentAuthAxiosInstance(creds.contentBaseUrl);
  else if (auth) instance = getAuthAxiosInstance(creds.baseUrl);
  else if (useContent) instance = getContentAxiosInstance(creds.contentBaseUrl);
  else instance = getAxiosInstance(creds.baseUrl);

  return instance.request({
    url,
    method: apiPath.apiMethod || method,
    data: Object.keys(body).length ? body : undefined,
    params: Object.keys(params).length ? params : undefined,
  });
};
